/**
 * RichTextField - lightweight textarea/input with an inline formatting
 * toolbar that wraps the current selection with ReportLab-friendly XML
 * markup so it renders bold / italic / underline / coloured text in the
 * generated consent PDFs (see backend/consent_docs.py).
 *
 * Supported markup (consumed by ReportLab `Paragraph`):
 *   <b>...</b>           bold
 *   <i>...</i>           italic
 *   <u>...</u>           underline
 *   <font color="#rrggbb">...</font>    colored text
 *
 * Keeps the underlying value as plain string with embedded tags - the
 * backend already passes section content directly to ReportLab.
 */
import { useRef, useState } from 'react';
import { TextB, TextItalic, TextUnderline, PaintBrushBroad, X } from '@phosphor-icons/react';

const PRESET_COLORS = [
  { name: 'Negro', value: '#1C1917' },
  { name: 'Gris', value: '#57534E' },
  { name: 'Verde corporativo', value: '#2C5545' },
  { name: 'Azul corporativo', value: '#1F3E80' },
  { name: 'Rojo', value: '#B91C1C' },
  { name: 'Granate', value: '#7F1D1D' },
  { name: 'Naranja', value: '#C2410C' },
  { name: 'Morado', value: '#6B21A8' },
];

export function RichTextField({
  value,
  onChange,
  placeholder,
  multiline = false,
  minRows = 4,
  testId,
  lang = 'es',
}) {
  const ref = useRef(null);
  const [showColors, setShowColors] = useState(false);
  const [customColor, setCustomColor] = useState('#000000');

  const wrap = (open, close) => {
    const el = ref.current;
    if (!el) return;
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? start;
    const before = value.slice(0, start);
    const selection = value.slice(start, end);
    const after = value.slice(end);
    const next = `${before}${open}${selection || ''}${close}${after}`;
    onChange(next);
    // restore selection so the user can keep typing inside the tag
    requestAnimationFrame(() => {
      const newStart = before.length + open.length;
      const newEnd = newStart + selection.length;
      el.focus();
      el.setSelectionRange(newStart, newEnd);
    });
  };

  const insertColor = (hex) => {
    wrap(`<font color="${hex}">`, '</font>');
    setShowColors(false);
  };

  const clearFormatting = () => {
    const el = ref.current;
    if (!el) return;
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? start;
    if (end === start) return;
    let selection = value.slice(start, end);
    // Remove the basic tags we know about (lightweight cleanup).
    selection = selection
      .replace(/<\/?b>/gi, '')
      .replace(/<\/?i>/gi, '')
      .replace(/<\/?u>/gi, '')
      .replace(/<font[^>]*>/gi, '')
      .replace(/<\/font>/gi, '');
    const next = `${value.slice(0, start)}${selection}${value.slice(end)}`;
    onChange(next);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start, start + selection.length);
    });
  };

  const btn = 'inline-flex items-center justify-center h-7 w-7 rounded-sm border border-stone-200 bg-white text-stone-600 hover:bg-stone-100 hover:text-stone-900 transition-colors';

  const Toolbar = (
    <div className="flex items-center gap-1 mb-1 relative">
      <button type="button" data-testid={testId ? `${testId}-bold` : undefined}
        onMouseDown={(e) => e.preventDefault()} onClick={() => wrap('<b>', '</b>')}
        title={lang === 'es' ? 'Negrita (texto seleccionado)' : 'Bold'} className={btn}>
        <TextB size={13} weight="bold" />
      </button>
      <button type="button" data-testid={testId ? `${testId}-italic` : undefined}
        onMouseDown={(e) => e.preventDefault()} onClick={() => wrap('<i>', '</i>')}
        title={lang === 'es' ? 'Cursiva' : 'Italic'} className={btn}>
        <TextItalic size={13} />
      </button>
      <button type="button" data-testid={testId ? `${testId}-underline` : undefined}
        onMouseDown={(e) => e.preventDefault()} onClick={() => wrap('<u>', '</u>')}
        title={lang === 'es' ? 'Subrayado' : 'Underline'} className={btn}>
        <TextUnderline size={13} />
      </button>
      <span className="w-px h-5 bg-stone-200 mx-1" />
      <button type="button" data-testid={testId ? `${testId}-color` : undefined}
        onMouseDown={(e) => e.preventDefault()} onClick={() => setShowColors(s => !s)}
        title={lang === 'es' ? 'Color del texto' : 'Text color'} className={btn}>
        <PaintBrushBroad size={13} />
      </button>
      <button type="button" data-testid={testId ? `${testId}-clear` : undefined}
        onMouseDown={(e) => e.preventDefault()} onClick={clearFormatting}
        title={lang === 'es' ? 'Quitar formato' : 'Clear formatting'} className={btn}>
        <X size={13} />
      </button>
      {showColors && (
        <div className="absolute z-50 top-9 left-0 bg-white border border-stone-200 rounded-sm shadow-md p-2 w-64" onMouseDown={(e) => e.preventDefault()}>
          <p className="text-[10px] uppercase tracking-wide text-stone-500 mb-1 font-semibold">{lang === 'es' ? 'Colores presets' : 'Preset colors'}</p>
          <div className="grid grid-cols-4 gap-1.5 mb-2">
            {PRESET_COLORS.map(c => (
              <button key={c.value} type="button"
                data-testid={testId ? `${testId}-color-${c.value.slice(1)}` : undefined}
                title={c.name}
                onClick={() => insertColor(c.value)}
                className="h-6 rounded-sm border border-stone-200 hover:scale-105 transition-transform"
                style={{ backgroundColor: c.value }}
              />
            ))}
          </div>
          <p className="text-[10px] uppercase tracking-wide text-stone-500 mb-1 font-semibold">{lang === 'es' ? 'Personalizado' : 'Custom'}</p>
          <div className="flex items-center gap-2">
            <input type="color" value={customColor} onChange={(e) => setCustomColor(e.target.value)} className="h-7 w-10 rounded-sm border border-stone-200 cursor-pointer p-0" />
            <input type="text" value={customColor} onChange={(e) => setCustomColor(e.target.value)} className="h-7 flex-1 text-xs font-mono border border-stone-200 rounded-sm px-2" placeholder="#RRGGBB" />
            <button type="button" onClick={() => insertColor(customColor)} className="h-7 px-2 text-xs bg-stone-700 text-white rounded-sm hover:bg-stone-800">
              {lang === 'es' ? 'Aplicar' : 'Apply'}
            </button>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-0.5">
      {Toolbar}
      {multiline ? (
        <textarea
          ref={ref}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={minRows}
          data-testid={testId}
          className="w-full border border-stone-200 rounded-sm p-2 text-sm font-mono leading-relaxed resize-y focus:outline-none focus:ring-1 focus:ring-emerald-500"
        />
      ) : (
        <input
          ref={ref}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          data-testid={testId}
          className="w-full border border-stone-200 rounded-sm px-2 py-1.5 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-emerald-500"
        />
      )}
    </div>
  );
}

export default RichTextField;
